import { z } from 'zod';

import { TypeSalesTermsAndConditions } from '../models/contentful-generated';
import { ContentfulDocumentSchema } from './contentful-document.validator';
import { ContentfulSysSchema } from './contentful-sys.validator';
import { FooterCountryInfoEntrySchema, LocalizedFooterCountryInfoEntrySchema } from './footer-country-info.validator';
import { LocalizedSeoMetadataEntrySchema, SeoMetadataEntrySchema } from './seo-metadata.validator';

export const SalesTermsAndConditionsEntrySchema = z.object({
  sys: ContentfulSysSchema,
  fields: z.looseObject({
    internalName: z.string(),
    slug: z.string(),
    content: ContentfulDocumentSchema.optional().nullable(),
    ole: FooterCountryInfoEntrySchema,
    seoMetadata: SeoMetadataEntrySchema.optional().nullable()
  }),
  metadata: z
    .object({
      tags: z.array(z.any()).optional().nullable()
    })
    .optional()
    .nullable()
});

export const LocalizedSalesTermsAndConditionsEntrySchema = z.object({
  sys: ContentfulSysSchema,
  fields: z.looseObject({
    internalName: z.record(z.string(), z.string()),
    slug: z.record(z.string(), z.string()),
    content: z.record(z.string(), ContentfulDocumentSchema.optional().nullable()).optional().nullable(),
    ole: z.record(z.string(), LocalizedFooterCountryInfoEntrySchema),
    seoMetadata: z.record(z.string(), LocalizedSeoMetadataEntrySchema.optional().nullable()).optional().nullable()
  }),
  metadata: z
    .object({
      tags: z.array(z.any()).optional().nullable()
    })
    .optional()
    .nullable()
});

export function validateSalesTermsAndConditionsEntry(data: TypeSalesTermsAndConditions) {
  return SalesTermsAndConditionsEntrySchema.safeParse(data);
}

export function validateLocalizedSalesTermsAndConditionsEntry(data: unknown) {
  return LocalizedSalesTermsAndConditionsEntrySchema.safeParse(data);
}

/**
 * Returns validation result.
 *
 * @param data
 */
export function validateSalesTermsAndConditionsEntries(data: TypeSalesTermsAndConditions[]) {
  return z.array(SalesTermsAndConditionsEntrySchema).safeParse(data);
}

/**
 * Returns validation result.
 *
 * @param data
 */
export function validateLocalizedSalesTermsAndConditionsEntries(data: unknown) {
  return z.array(LocalizedSalesTermsAndConditionsEntrySchema).safeParse(data);
}

/**
 * Returns valid entries only. In most cases, this is preferred,
 * It allows us to still show some entries instead of showing nothing when only a single entry is invalid.
 *
 * @param data
 */
export function validateAndFilterSalesTermsAndConditionsEntries(data: TypeSalesTermsAndConditions[]) {
  if (!data) return [];

  return data.reduce((acc: TypeSalesTermsAndConditions[], entry) => {
    const validationResult = validateSalesTermsAndConditionsEntry(entry);
    if (validationResult.success) {
      acc.push(validationResult.data as unknown as TypeSalesTermsAndConditions);
    } else {
      console.warn(
        `Validation failed for localized sales term and condition entry with id: '${entry?.sys?.id}'. It will be filtered out. Error: ${validationResult.error.message}`
      );
    }
    return acc;
  }, []);
}

/**
 * Returns valid entries only. In most cases, this is preferred,
 * It allows us to still show some entries instead of showing nothing when only a single entry is invalid.
 *
 * @param data
 */
export function validateAndFilterLocalizedSalesTermsAndConditionsEntries(data: any[]) {
  if (!data) return [];

  return data.reduce((acc: any, entry: any) => {
    const validationResult = validateLocalizedSalesTermsAndConditionsEntry(entry);
    if (validationResult.success) {
      acc.push(validationResult.data);
    } else {
      console.warn(
        `Validation failed for localized sales term and condition entry with id: '${entry?.sys?.id}'. It will be filtered out. Error: ${validationResult.error.message}`
      );
    }
    return acc;
  }, []);
}
